import { ApplicationRef, ComponentFactoryResolver, Inject, Injectable, Injector, Renderer2, RendererFactory2 } from '@angular/core';
import { v4 as uuid } from 'uuid';

import { EventBus, EventBusBase } from '@skylab-angular-shared/core';
import { isFunction } from '@skylab-shared/utils';

import { getComponentRefElement } from '../utils/component-ref-element';
import { $, $Element } from '../utils/element';
import { IPosition, PositionAlignment } from '../utils/position-alignment';
import { UIEvents } from '../utils/ui-events';

import { PopoverController } from '../wc-controllers/popover.controller';
import { MenuContextComponent } from './menu-context/menu-context.component';
import { MenuComponentOptions } from './menu-ins.component';
import { MenuOptionsComponent } from './menu-options/menu-options.component';
import { MenuPointerComponent } from './menu-pointer/menu-pointer.component';
import { MenuSubComponent } from './menu-sub/menu-sub.component';
import { MenuItem, MenuItemOption } from './menu.model';

@Injectable({
    providedIn: 'root'
})
export class MenuService {

    private overlay!: HTMLInsPopoverElement;
    private renderer: Renderer2;

    public constructor(
        public componentFactoryResolver: ComponentFactoryResolver,
        public injector: Injector,
        public appRef: ApplicationRef,
        rendererFactory: RendererFactory2,
        @Inject(EventBus) public eventBus: EventBusBase,
        protected popoverController: PopoverController) {
            this.renderer = rendererFactory.createRenderer(null, null);
        }

    public async createMenu<TContext>(items: MenuItem<TContext>[], event: MouseEvent, options?: MenuComponentOptions<TContext>): Promise<void> {

        const element = $(event.target as HTMLElement);
        const activeItems = items.filter(item => typeof item.disabled === 'function' ? !item.disabled(options?.context) : !item.disabled);
        this.showMenuContext(element, activeItems as MenuItem<any>[], options?.context, { left: event.clientX, top: event.clientY });

        // TODO: WIP - SL-7646 Refactor menu kontekstowych
        // if (this.overlay) {
        //     this.overlay.dismiss();
        // }
        // const overlay = this.overlay = await this.createList(items, rect, options);
        // const overlayDismiss = () => this.overlay.dismiss();
        // const removeOnMouseDown = this.renderer.listen(this.document, 'mousedown', (_event: PointerEvent) => {
        //     this.overlay.dismiss();
        // });
        // this.document.addEventListener('resize', overlayDismiss);
        // this.document.addEventListener('scroll', overlayDismiss, true);

        // overlay.onDidDismiss().then(() => {
        //     removeOnMouseDown();
        //     document.removeEventListener('resize', overlayDismiss);
        //     document.removeEventListener('scroll', overlayDismiss);
        // });

        // overlay.present();
    }

    public showMenuPointer(node: $Element, collection: MenuItem[], context: unknown, onClose: ($event: Event) => void): (() => void) | undefined {
        if (node.hasAttr('menu-id')) return undefined;

        const menuId = uuid();
        node.setAttr('menu-id', menuId);

        const componentRef = this.componentFactoryResolver
            .resolveComponentFactory(MenuPointerComponent)
            .create(this.injector);

        this.clearActivness(collection);
        componentRef.instance.collection = collection;
        componentRef.instance.context = context;
        componentRef.instance.triggerElement = node.element;

        let isClosed = false;
        componentRef.instance.close = ($event?) => {
            if (isClosed) return;
            isClosed = true;
            this.eventBus.emit(UIEvents.MenuClosed, menuId);
            node.removeAttr('menu-id');
            if (isFunction(onClose)) {
                onClose($event);
            }
            componentRef.instance.unbindEvents();
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
            $('skl-menu-container').hide();
        };
        componentRef.instance.onEsc = () => this.focusTrigger(node);
        this.appRef.attachView(componentRef.hostView);

        componentRef.changeDetectorRef.detectChanges();

        const componentElement = getComponentRefElement(componentRef.hostView);
        componentElement.setAttr('tabindex', -1);
        componentElement.setAttr('menu-id', menuId);
        componentElement.setCss({ visibility: 'hidden' });
        $('skl-menu-container').show();
        $('skl-menu-container').append(componentElement.element);
        setTimeout(() => {
            PositionAlignment.setPositionByNodeWithPointer(componentElement.find('.js-component-content'), node, 'border-top-color');
            componentElement.setCss({ visibility: 'visible' });
            componentElement.focus();
        });

        return componentRef.instance.close;

    }

    public showMenuContext(node: $Element, collection: MenuItem[], context: unknown, pos: IPosition): (() => void) | undefined {
        if (node.hasAttr('menu-id')) return undefined;

        const menuId = uuid();
        node.setAttr('menu-id', menuId);

        const componentRef = this.componentFactoryResolver
            .resolveComponentFactory(MenuContextComponent)
            .create(this.injector);

        this.clearActivness(collection);
        componentRef.instance.collection = collection;
        componentRef.instance.context = context;
        componentRef.instance.triggerElement = node.element;

        let isClosed = false;
        componentRef.instance.close = ($event?) => {
            if (isClosed) return;
            isClosed = true;
            this.eventBus.emit(UIEvents.MenuClosed, menuId);
            node.removeAttr('menu-id');
            componentRef.instance.unbindEvents();
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
            $('skl-menu-container').hide();
        };
        componentRef.instance.onEsc = () => this.focusTrigger(node);
        this.appRef.attachView(componentRef.hostView);

        componentRef.changeDetectorRef.detectChanges();

        const componentElement = getComponentRefElement(componentRef.hostView);
        componentElement.setAttr('tabindex', -1);
        componentElement.setAttr('menu-id', menuId);
        componentElement.setCss({ visibility: 'hidden' });
        $('skl-menu-container').show();
        $('skl-menu-container').append(componentElement.element);
        setTimeout(() => {
            PositionAlignment.setPositionByAnchor(componentElement.find('.js-component-content'), pos);
            componentElement.setCss({ visibility: 'visible' });
            componentElement.focus();
        });

        return componentRef.instance.close;
    }

    public showMenuOptions(node: $Element, collection: MenuItemOption[], pos: IPosition): (() => void) | undefined {
        if (node.hasAttr('menu-id')) return undefined;

        const menuId = uuid();
        node.setAttr('menu-id', menuId);

        const componentRef = this.componentFactoryResolver
            .resolveComponentFactory(MenuOptionsComponent)
            .create(this.injector);

        this.clearActivness(collection);
        componentRef.instance.collection = collection;
        componentRef.instance.triggerElement = node.element;

        let isClosed = false;
        componentRef.instance.close = ($event?) => {
            if (isClosed) return;
            isClosed = true;
            this.eventBus.emit(UIEvents.MenuClosed, menuId);
            node.removeAttr('menu-id');
            componentRef.instance.unbindEvents();
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
            $('skl-menu-container').hide();
        };
        componentRef.instance.onEsc = () => this.focusTrigger(node);
        this.appRef.attachView(componentRef.hostView);

        componentRef.changeDetectorRef.detectChanges();

        const componentElement = getComponentRefElement(componentRef.hostView);
        componentElement.setAttr('tabindex', -1);
        componentElement.setAttr('menu-id', menuId);
        componentElement.setCss({ visibility: 'hidden' });
        $('skl-menu-container').show();
        $('skl-menu-container').append(componentElement.element);
        setTimeout(() => {
            PositionAlignment.setPositionByAnchor(componentElement.find('.js-component-content'), pos);
            componentElement.setCss({ visibility: 'visible' });
            componentElement.focus();
        });

        return componentRef.instance.close;
    }

    public showMenuSub(node: HTMLElement | $Element, collection: MenuItem[], context: unknown, closeParent: () => void, parentMenuId: string): (() => void) | undefined {
        const nodeEl = node instanceof HTMLElement ? $(node) : node;
        if (nodeEl.hasAttr('menu-id')) return undefined;

        const menuItemHeight = 45;
        const menuId = uuid();
        nodeEl.setAttr('menu-id', menuId);

        const componentRef = this.componentFactoryResolver
            .resolveComponentFactory(MenuSubComponent)
            .create(this.injector);

        this.clearActivness(collection);
        componentRef.instance.collection = collection;
        componentRef.instance.context = context;
        componentRef.instance.closeParent = closeParent;
        componentRef.instance.parentMenuId = parentMenuId;
        let isClosed = false;
        componentRef.instance.close = ($event?) => {
            if (isClosed) return;
            isClosed = true;
            this.eventBus.emit(UIEvents.MenuClosed, menuId);
            nodeEl.removeAttr('menu-id');
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
        };
        this.appRef.attachView(componentRef.hostView);

        componentRef.changeDetectorRef.detectChanges();

        const componentElement = getComponentRefElement(componentRef.hostView);
        componentElement.setAttr('menu-id', menuId);
        componentElement.setCss({ visibility: 'hidden' });
        nodeEl.append(componentElement.element);
        setTimeout(() => {
            PositionAlignment.setPositionByNode(componentElement.find('.js-component-content'), nodeEl, 'rightleft', menuItemHeight);
            componentElement.setCss({ visibility: 'visible' });
        });

        return componentRef.instance.close;
    }

    public clearActivness(collection: MenuItem[]): void {
        collection.forEach(i => i.isActive = false);
    }

    private focusTrigger(node: $Element): void {
        const trigger = node.element.shadowRoot?.querySelector<HTMLElement>('.button');
        (trigger ?? node.element).focus();
    }

    // private createList<TContext = unknown>(items: MenuItem<TContext>[], rect: DOMRect | ClientRect, options?: MenuComponentOptions<TContext>): Promise<HTMLInsPopoverElement> {
    //     return this.popoverController.create({
    //         anchor: rect,
    //         removeBackdrop: true,
    //         component: MenuInsComponent,
    //         componentProps: {
    //             items,
    //             options,
    //         },
    //     });
    // }
}
